import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  CardActions,
  Box,
  Paper,
  useTheme,
  useMediaQuery,
  Fade,
  Grow,
  CircularProgress,
  Divider,
  ButtonGroup
} from '@mui/material';
import   CodeSite   from   '../../codesite'   
import { Link as RouterLink } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import ActivateIcon from '@mui/icons-material/CreditScore';
import BalanceIcon from '@mui/icons-material/AccountBalance';
import AccountIcon from '@mui/icons-material/AccountCircle';
import NetworkIcon from '@mui/icons-material/SwapHorizontalCircle';
import QrCodeIcon from '@mui/icons-material/QrCode';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useAuth } from '../../context/AuthContext';
import useHomePageContent from '../../hooks/useHomePageContent';
import CampaignList from '../../components/CampaignList';
import { BusinessService } from '../../services/api';
import styles from './HomePage.module.css';

const HomePage: React.FC = () => {
  const { isAuthenticated, role } = useAuth();
  const { content } = useHomePageContent();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [visibleSections, setVisibleSections] = useState({
    hero: false,
    quickAccess: false,
    network: false,
    features: false,
    howItWorks: false,
    cta: false
  });
  const [campaigns, setCampaigns] = useState<any>([]);

  // Simulate loading and reveal sections progressively
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
      setVisibleSections(prev => ({ ...prev, hero: true }));
    }, 800);

    const sectionsTimers = [
      { section: 'quickAccess', delay: 1000 },
      { section: 'network', delay: 1300 },
      { section: 'features', delay: 1600 },
      { section: 'howItWorks', delay: 1900 },
      { section: 'cta', delay: 2200 }
    ];

    sectionsTimers.forEach(({ section, delay }) => {
      setTimeout(() => {
        setVisibleSections(prev => ({ ...prev, [section]: true }));
      }, delay);
    });

    return () => {
      clearTimeout(timer);
      sectionsTimers.forEach(({ delay }) => clearTimeout(delay));
    };
  }, []);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await BusinessService.allCampaigns();
        setCampaigns(response);
      } catch (err) {
        console.error('Failed to fetch campaigns', err);
      }
    };

    fetchCampaigns();
  }, []);

  const roleCards = content.featuresSection.roles.map((role, index) => {
    const icons = [
      <BusinessIcon fontSize="large" className={styles.cardIcon} />,
      <PeopleIcon fontSize="large" className={styles.cardIcon} />,
      <PersonIcon fontSize="large" className={styles.cardIcon} />
    ];

    return {
      title: role.title,
      description: role.description,
      icon: icons[index] || icons[0],
      linkTo: `/register?role=${role.title.toLowerCase()}`,
      linkText: role.buttonText
    };
  });

  const quickActions = [
    {
      title: content.quickAccessSection.activateCard.title,
      description: content.quickAccessSection.activateCard.description,
      icon: <ActivateIcon fontSize="large" className={styles.actionIcon} />,
      content: (
        <div className={styles.activateCardContent}>
          <div className={styles.activationMethods}>
            <div className={styles.activationMethod}>
              <div className={styles.activationMethodIcon}>
                <StorefrontIcon />
              </div>
              <Typography variant="body2" className={styles.activationMethodText}>
                <strong>{content.quickAccessSection.activateCard.business.title}</strong> {content.quickAccessSection.activateCard.business.description}
              </Typography>
              <Button 
                variant="contained" 
                color="primary"
                component={RouterLink}
                to="/activate-business"
                className={styles.activationButton}
                size="small"
              >
                {content.quickAccessSection.activateCard.business.buttonText}
              </Button>
            </div>
            
            <Divider orientation="vertical" flexItem className={styles.activationDivider} />
            
            <div className={styles.activationMethod}>
              <div className={styles.activationMethodIcon}>
                <PersonIcon />
              </div>
              <Typography variant="body2" className={styles.activationMethodText}>
                <strong>{content.quickAccessSection.activateCard.customer.title}</strong> {content.quickAccessSection.activateCard.customer.description}
              </Typography>
              <Button 
                variant="contained" 
                color="primary"
                component={RouterLink}
                to="/activate-customer"
                className={styles.activationButton}
                size="small"
              >
                {content.quickAccessSection.activateCard.customer.buttonText}
              </Button>
            </div>
          </div>
          
          <Divider className={styles.horizontalDivider}>
            <Typography variant="body2" color="textSecondary">OR</Typography>
          </Divider>
          
          <div className={styles.qrCodeActivation}>
            <div className={styles.qrCodeIcon}>
              <QrCodeIcon fontSize="large" />
            </div>
            <Typography variant="subtitle1" className={styles.qrCodeText}>
              {content.quickAccessSection.activateCard.qrCode.description}
            </Typography>
          </div>
        </div>
      )
    },
    {
      title: content.quickAccessSection.checkBalance.title,
      description: content.quickAccessSection.checkBalance.description,
      icon: <BalanceIcon fontSize="large" className={styles.actionIcon} />,
      linkTo: "/check-balance",
      buttonText: content.quickAccessSection.checkBalance.buttonText
    },
    {
      title: content.quickAccessSection.cashoutCard.title,
      description: content.quickAccessSection.cashoutCard.description,
      icon: <BalanceIcon fontSize="large" className={styles.actionIcon} />,
      linkTo: "/cashout",
      buttonText: content.quickAccessSection.cashoutCard.buttonText
    },
    {
      title: content.quickAccessSection.registerCard.title,
      description: content.quickAccessSection.registerCard.description,
      icon: <AccountIcon fontSize="large" className={styles.actionIcon} />,
      linkTo: "/register?role=customer",
      buttonText: content.quickAccessSection.registerCard.buttonText
    }
  ];

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" className={styles.loadingText}>
          Loading amazing experiences...
        </Typography>
      </div>
    );
  }

  return (
    <div className={styles.homePage}>
      {/* Hero Section */}
      <Fade in={visibleSections.hero} timeout={1000}>
        <div className={styles.heroSection}>
          <Container maxWidth="lg">
            <div className={styles.heroContainer}>
              <div className={styles.heroContent}>
                <Typography variant="h2" component="h1" className={styles.heroTitle}>
                  {content.heroSection.title}
                </Typography>
                <Typography variant="h5" component="h2" className={styles.heroSubtitle}>
                  {content.heroSection.subtitle}
                </Typography>
                {!isAuthenticated ? (
                  <Box sx={{ mt: 4 }}>
                    <Button 
                      component={RouterLink} 
                      to="/register" 
                      variant="contained" 
                      color="primary" 
                      size="large"
                      sx={{ mr: 2, mb: isMobile ? 2 : 0 }}
                      className={styles.pulseButton}
                    >
                      {content.heroSection.getStartedButton}
                    </Button>
                    <Button 
                      component={RouterLink} 
                      to="/login" 
                      variant="outlined" 
                      color="primary" 
                      size="large"
                    >
                      {content.heroSection.loginButton}
                    </Button>
                  </Box>
                ) : (
                  <Button 
                    component={RouterLink} 
                    to="/dashboard" 
                    variant="contained" 
                    color="primary" 
                    size="large"
                    sx={{ mt: 4 }}
                    className={styles.pulseButton}
                  >
                    {content.heroSection.dashboardButton}
                  </Button>
                )}
              </div>
              <div className={styles.heroImageContainer}>
                <div className={styles.giftCard}>
                  <div className={styles.giftCardInner}>
                    <div className={styles.giftCardTop}>
                      <div className={styles.giftCardChip}></div>
                    </div>
                    <div className={styles.giftCardBottom}>
                      <div className={styles.giftCardText}>GIFT CARD</div>
                      <div className={styles.giftCardValue}>{content.heroSection.giftCardValue}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </div>
      </Fade>

      {/* Quick Access Section */}
      <Fade in={visibleSections.quickAccess} timeout={1000}>
        <div className={styles.quickAccessSection}>
          <Container maxWidth="lg">
            <Typography variant="h4" component="h2" align="center" gutterBottom>
              {content.quickAccessSection.title}
            </Typography>
            <Typography variant="body1" align="center" paragraph className={styles.sectionDescription}>
              {content.quickAccessSection.description}
            </Typography>
            
            <div className={styles.quickAccessContainer}>
              {/* First card (Activate) in its own row */}
              <Grow
                in={visibleSections.quickAccess}
                timeout={500}
              >
                <div className={styles.activateCardWide}>
                  <div className={styles.quickAccessIcon}>
                    {quickActions[0].icon}
                  </div>
                  <Typography variant="h5" component="h3" className={styles.quickAccessTitle}>
                    {quickActions[0].title}
                  </Typography>
                  <Typography variant="body2" className={styles.quickAccessDescription}>
                    {quickActions[0].description}
                  </Typography>
                  
                  {quickActions[0].content}
                </div>
              </Grow>

              {/* Second row with two remaining cards */}
              <div className={styles.secondaryCardsRow}>
                {quickActions.slice(1).map((action, index) => (
                  <Grow
                    in={visibleSections.quickAccess}
                    timeout={500 + ((index + 1) * 200)}
                    key={index}
                  >
                    <div className={styles.quickAccessCard}>
                      <div className={styles.quickAccessIcon}>
                        {action.icon}
                      </div>
                      <Typography variant="h5" component="h3" className={styles.quickAccessTitle}>
                        {action.title}
                      </Typography>
                      <Typography variant="body2" className={styles.quickAccessDescription}>
                        {action.description}
                      </Typography>
                      
                      <Button 
                        variant="contained" 
                        color="primary"
                        fullWidth
                        component={RouterLink}
                        to={action.linkTo || "/"}
                        className={styles.quickAccessButton}
                      >
                        {action.buttonText}
                      </Button>
                    </div>
                  </Grow>
                ))}
              </div>
            </div>
          </Container>
        </div>
      </Fade>

      {/* Network Explanation Section */}
      <Fade in={visibleSections.network} timeout={1000}>
        <div className={styles.networkSection}>
          <Container maxWidth="lg">
            <div className={styles.networkContainerCentered}>
              <div className={styles.networkContent}>
                <Typography variant="h3" component="h2" gutterBottom className={styles.networkTitle}>
                  {content.networkSection.title}
                </Typography>
                <Typography variant="body1" paragraph className={styles.networkDescription}>
                  {content.networkSection.description}
                </Typography>
                
                <div className={styles.networkHighlight}>
                  <div className={styles.networkHighlightContent}>
                    <Typography variant="h5" component="h3" className={styles.networkHighlightTitle}>
                      {content.networkSection.highlight.title}
                    </Typography>
                    <Typography variant="body2" className={styles.networkHighlightText}>
                      {content.networkSection.highlight.description}
                    </Typography>
                  </div>
                  <div className={styles.networkHighlightNumber}>
                    <span>{content.networkSection.highlight.numberText}</span>
                    <small>{content.networkSection.highlight.numberLabel}</small>
                  </div>
                </div>
                
                <div className={styles.networkPoints}>
                  {content.networkSection.points.map((point, index) => (
                    <div className={styles.networkPoint} key={index}>
                      <div className={styles.networkPointIcon}>
                        {index === 0 ? <BusinessIcon /> : 
                         index === 1 ? <NetworkIcon /> : 
                                       <BalanceIcon />}
                      </div>
                      <div>
                        <Typography variant="h6" gutterBottom>{point.title}</Typography>
                        <Typography variant="body2">{point.description}</Typography>
                      </div>
                    </div>
                  ))}
                </div>
                <Button 
                  component={RouterLink} 
                  to="/register?role=business" 
                  variant="contained" 
                  color="primary" 
                  size="large"
                  className={styles.networkButton}
                >
                  {content.networkSection.buttonText}
                </Button>
              </div>
            </div>
          </Container>
        </div>
      </Fade>

      {/* Features Section */}
      <Fade in={visibleSections.features} timeout={1000}>
        <Container maxWidth="lg" className={styles.featuresSection}>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            {content.featuresSection.title}
          </Typography>
          <Typography variant="subtitle1" align="center" paragraph className={styles.sectionDescription}>
            {content.featuresSection.description}
          </Typography>

          <div className={styles.roleCardsContainer}>
            {roleCards.map((card, index) => (
              <Grow
                in={visibleSections.features}
                timeout={500 + (index * 200)}
                key={index}
              >
                <div className={styles.roleCardWrapper}>
                  <Card className={styles.roleCard} variant="outlined">
                    <CardContent>
                      <Box className={styles.iconContainer}>
                        {React.cloneElement(card.icon, { style: { fontSize: '60px' } })}
                      </Box>
                      <Typography variant="h5" component="h3" gutterBottom className={styles.cardTitle}>
                        {card.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" className={styles.cardDescription}>
                        {card.description}
                      </Typography>
                    </CardContent>
                    {!isAuthenticated && (
                      <CardActions className={styles.cardActions}>
                        <Button 
                          size="large" 
                          color="primary" 
                          variant="contained"
                          fullWidth
                          component={RouterLink} 
                          to={card.linkTo || "/"}
                          className={card.title === 'Businesses' ? styles.pulseButton : ''}
                        >
                          {card.linkText}
                        </Button>
                      </CardActions>
                    )}
                  </Card>
                </div>
              </Grow>
            ))}
          </div>
        </Container>
      </Fade>

      {/* How It Works Section */}
      <Fade in={visibleSections.howItWorks} timeout={1000}>
        <Paper className={styles.howItWorksSection}>
          <Container maxWidth="lg">
            <Typography variant="h3" component="h2" align="center" gutterBottom>
              {content.howItWorksSection.title}
            </Typography>

            <div className={styles.stepsContainer}>
              {content.howItWorksSection.steps.map((step, index) => (
                <Grow
                  in={visibleSections.howItWorks}
                  timeout={500 + (index * 200)}
                  key={index}
                >
                  <div className={styles.stepWrapper}>
                    <Box className={styles.stepBox}>
                      <Typography variant="h2" className={styles.stepNumber}>{step.number}</Typography>
                      <Typography variant="h5" gutterBottom>
                        {step.title}
                      </Typography>
                      <Typography variant="body1">
                        {step.description}
                      </Typography>
                    </Box>
                  </div>
                </Grow>
              ))}
            </div>
          </Container>
        </Paper>
      </Fade>

      {/* CTA Section */}
      <Fade in={visibleSections.cta} timeout={1000}>
        <Container maxWidth="md" className={styles.ctaSection}>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            {content.ctaSection.title}
          </Typography>
          <Typography variant="subtitle1" align="center" paragraph>
            {content.ctaSection.description}
          </Typography>
          {!isAuthenticated ? (
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button 
                component={RouterLink} 
                to="/register" 
                variant="contained" 
                color="primary" 
                size="large"
                className={styles.pulseButton}
              >
                {content.ctaSection.registerButton}
              </Button>
              <Button 
                component={RouterLink} 
                to="/login" 
                variant="outlined" 
                color="primary" 
                size="large"
              >
                {content.ctaSection.loginButton}
              </Button>
            </Box>
          ) : (
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
              <Button 
                component={RouterLink} 
                to="/dashboard" 
                variant="contained" 
                color="primary" 
                size="large"
              >
                {content.ctaSection.dashboardButton}
              </Button>
            </Box>
          )}
        </Container>
      </Fade>

      {/* Campaigns Section */}
      <Fade in={campaigns.length > 0} timeout={1000}>
        <div className={styles.campaignsSection}>
          <Container maxWidth="lg">
            <Typography variant="h3" component="h2" align="center" gutterBottom>
              {content.campaignsSection.title}
            </Typography>
            <Typography variant="subtitle1" align="center" paragraph className={styles.sectionDescription}>
              {content.campaignsSection.description}
            </Typography>
            
            <CampaignList campaigns={campaigns} />
          </Container>
        </div>
      </Fade>
    </div>
  );
};

export default HomePage;
